import * as yup from "yup";
import { useForm } from "react-hook-form";
import { useEffect, useMemo } from "react";
import { useYupValidationResolver } from "../../../../hooks/useYupValidationResolver";
import { isVersionUS } from "../../../../services/versionService";

const validationSchema = yup.object({
    forename: yup.string().required("Field is required"),
    preferredName: yup.string(),
    middleNames: yup.string(),
    surname: yup.string().required("Field is required"),
    suffix: yup.string(),
    genderTypeId: yup.string().required("Field is required"),
    nationality: yup.object().shape({ id: yup.string(), label: yup.string() }).test(
        "not-empty",
        "Field is required",
        (value) => !!value && Object.keys(value).length > 0,
    ).required("Field is required"),
    citizenOfSchoolCountry: isVersionUS ? yup.boolean().required("Field is required") : yup.boolean(),
    dateOfBirth: yup.string().required("Field is required"),
    firstLanguage: yup.object().shape({ id: yup.string(), label: yup.string() }).test(
        "not-empty",
        "Field is required",
        (value) => value && Object.keys(value).length > 0,
    ).required("Field is required"),
    addressId: yup.string().required("Field is required"),
    hasSEN: !isVersionUS ? yup.boolean().required("Field is required") : yup.boolean(),
    sen: yup.array().of(yup.object().shape({
        senTypeId: !isVersionUS ? yup.string().required("Field is required") : yup.string(),
        otherDescription: yup.string().when("senTypeId", {
            is: (value) => value === "Other",
            then: (schema) => schema.required("Field is required"),
            otherwise: (schema) => schema,
        }),
    }).when("hasSEN", {
        is: true,
        then: (schema) => schema.min(1, "Field is required"),
        otherwise: (schema) => schema,
    })),
    senOtherDescription: yup.string(),
    sendDocument: yup.object().shape({
        documentId: yup.string(),
        fileName: yup.string(),
        uploadedAt: yup.date(),
    }),
    // disability: yup.boolean().required("Field is required"),
    safeguarding: !isVersionUS ? yup.string().required("Field is required") : yup.string(),
    safeguardingDocument: yup.object().shape({
        documentId: yup.string(),
        fileName: yup.string(),
        uploadedAt: yup.date(),
    }),
    outOfYearDocument: yup.object().shape({
        documentId: yup.string(),
        fileName: yup.string(),
        uploadedAt: yup.date(),
    }),
    dbsDocument: yup.object().shape({
        documentId: yup.string(),
        fileName: yup.string(),
        uploadedAt: yup.date(),
    }),
});

const emptyFields = {
    forename: "",
    preferredName: "",
    middleNames: "",
    surname: "",
    suffix: "",
    genderTypeId: "",
    nationality: undefined,
    citizenOfSchoolCountry: undefined,
    dateOfBirth: "",
    firstLanguage: undefined,
    addressId: "",
    hasSEN: undefined,
    sen: [],
    senOtherDescription: "",
    sendDocument: undefined,
    // disability: undefined,
    safeguarding: undefined,
    safeguardingDocument: undefined,
    outOfYearDocument: undefined,
    dbsDocument: undefined,
};

export const useStudentForm = (data) => {
    const defaultValues = useMemo(
        () => Object.fromEntries(Object.entries(emptyFields).map(([key, value]) => [key, key in data && data[key] !== null ? data?.[key] : value])),
        [data],
    );

    const resolver = useYupValidationResolver(validationSchema);

    const {
        control,
        handleSubmit,
        register,
        reset,
        watch,
        setValue,
        formState: { errors, isDirty: isDirtyForm, isSubmitted },
    } = useForm({
        resolver,
        defaultValues,
        mode: "onChange",
    });

    useEffect(() => {
        reset(defaultValues);
    }, [defaultValues]);

    return {
        control,
        register,
        errors,
        handleSubmit,
        reset,
        watch,
        setValue,
        isDirtyForm,
        isSubmitted,
    };
};
